/**
 * JWKS cache.
 *
 * Fetches the cerberus JWKS document once, caches it in-memory + sessionStorage
 * with a configurable TTL, and exposes a lookup by `kid`.
 *
 * Lazy & idempotent: concurrent callers share a single in-flight fetch.
 */
import { CERBERUS_API_URL, JWKS_CACHE_TTL_MS, jwksUrl } from './config'
import type { JsonWebKey, JsonWebKeySet } from './types'

const CACHE_KEY = 'cerberus_jwks_cache_v1'

interface CacheEntry {
  url: string
  fetched_at: number
  jwks: JsonWebKeySet
}

let memCache: CacheEntry | null = null
let inflight: Promise<JsonWebKeySet> | null = null

function readSession(): CacheEntry | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CacheEntry
    if (
      !parsed ||
      typeof parsed.fetched_at !== 'number' ||
      typeof parsed.url !== 'string' ||
      !parsed.jwks ||
      !Array.isArray(parsed.jwks.keys)
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeSession(entry: CacheEntry): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    /* quota / private mode */
  }
}

function isFresh(entry: CacheEntry, url: string, ttlMs: number): boolean {
  return entry.url === url && Date.now() - entry.fetched_at < ttlMs
}

export interface FetchJwksOptions {
  apiUrl?: string
  ttlMs?: number
  /** Force re-fetch even if cached. */
  force?: boolean
  /** Injected fetch (tests). */
  fetchImpl?: typeof fetch
}

async function fetchJwks(
  opts: FetchJwksOptions = {},
): Promise<JsonWebKeySet> {
  const apiUrl = opts.apiUrl ?? CERBERUS_API_URL
  const ttl = opts.ttlMs ?? JWKS_CACHE_TTL_MS
  const url = jwksUrl(apiUrl)

  if (!opts.force) {
    if (memCache && isFresh(memCache, url, ttl)) return memCache.jwks
    const fromSession = readSession()
    if (fromSession && isFresh(fromSession, url, ttl)) {
      memCache = fromSession
      return fromSession.jwks
    }
  }

  if (inflight) return inflight

  const fetchFn = opts.fetchImpl ?? fetch
  inflight = (async () => {
    const res = await fetchFn(url, { method: 'GET' })
    if (!res.ok) {
      throw new Error(`JWKS fetch failed: HTTP ${res.status}`)
    }
    const jwks = (await res.json()) as JsonWebKeySet
    if (!jwks || !Array.isArray(jwks.keys)) {
      throw new Error('JWKS fetch returned malformed body')
    }
    const entry: CacheEntry = { url, fetched_at: Date.now(), jwks }
    memCache = entry
    writeSession(entry)
    return jwks
  })()

  try {
    return await inflight
  } finally {
    inflight = null
  }
}

export async function getJwk(
  kid: string,
  opts: FetchJwksOptions = {},
): Promise<JsonWebKey | null> {
  const jwks = await fetchJwks(opts)
  const found = jwks.keys.find((k) => k.kid === kid)
  if (found) return found
  // Kid miss — JWKS may have rotated. Force-refetch once, ignoring caches.
  const fresh = await fetchJwks({ ...opts, force: true })
  return fresh.keys.find((k) => k.kid === kid) ?? null
}

/** Test helper — clears in-memory + sessionStorage caches. */
export function _resetJwksCache(): void {
  memCache = null
  inflight = null
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(CACHE_KEY)
    }
  } catch {
    /* noop */
  }
}
